import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { SearchEmptyState } from '../../shared/search_empty_state/search_empty_state.js';
import type { FlightExplorerPageCopy } from './explorer_copy.js';
import { useExplorerEnv, type FlightExplorerPlaces } from './explorer_env.js';
import { legSearchParams } from './explorer_request.js';
import type { ExplorerState } from './explorer_state.js';
import type { FlightSearchParams, LegFailure } from './search_source.js';

/** A cidade inteira ("todos os aeroportos") da busca que estourou o prazo: o lado
 *  metro do pedido, destino antes de origem. Só a busca de abertura de um pedido
 *  binário — no multi, re-formar o pedido apagaria o roteiro; na volta, a ida já
 *  fixou os aeroportos. */
function wholeCityOf(state: ExplorerState, places: FlightExplorerPlaces) {
  if (state.request.mode === 'multi_leg' || state.activeLeg !== 0) return null;
  const params = legSearchParams(state.request, 0);
  for (const side of ['destination', 'origin'] as const) {
    const airports = places.metroAirports(params[side]);
    if (airports.length > 0) return { params, side, metro: params[side], airports };
  }
  return null;
}

/** Rótulos das duas ações da página de desfecho (`SearchEmptyState`): os mesmos
 *  nos dois tons. */
export function useSearchEmptyCopy() {
  const { copy } = useExplorerEnv();
  return { newSearchLabel: copy.page.newSearch, retryLabel: copy.page.retry };
}

type FailureText = { title: string; description: string | undefined };

/** O texto de cada falha com desfecho próprio: o prazo estourado num aeroporto
 *  só, a política do viajante que não avaliou (#5376) e a busca que venceu no
 *  fornecedor (#5588). */
const FAILURE_TEXT: Record<LegFailure, (page: FlightExplorerPageCopy) => FailureText> = {
  timeout: (page) => ({ title: page.timeout, description: page.timeoutHint }),
  policy_evaluation: (page) => ({
    title: page.policyEvaluation,
    description: page.policyEvaluationHint,
  }),
  search_expired: (page) => ({ title: page.searchExpired, description: page.searchExpiredHint }),
};

/**
 * Falha de upstream da perna ativa (#2587): a MESMA página do zero real, em tom
 * `error` — o título sai como `alert`. O prazo estourado tem desfecho próprio
 * (#4465): a tela diz que a busca demorou demais e, quando ela cobria a cidade
 * inteira, troca o "Tentar novamente" (que repetiria a MESMA busca pesada) por
 * atalhos que buscam um aeroporto dela. A política do viajante que não avaliou
 * (#5376) é terminal: repetir falharia igual e não há busca sem o viajante, então
 * a tela manda falar com o administrador da empresa e só oferece "Nova busca".
 * A busca vencida no fornecedor (#5588) diz que expirou e a ação principal vira
 * "Buscar de novo": refaz a busca com os mesmos critérios (`explorer_retry.ts`).
 * CONGELADO: sem ações, como o `ExplorerEmpty`.
 */
export function ExplorerError({
  state,
  frozen,
  onRetry,
  onNewSearch,
  onSearch,
}: Readonly<{
  state: ExplorerState;
  frozen: boolean;
  onRetry: () => void;
  onNewSearch?: () => void;
  onSearch: (params: FlightSearchParams) => void;
}>) {
  const { copy: strings, places } = useExplorerEnv();
  const emptyCopy = useSearchEmptyCopy();
  const leg = state.legs[state.activeLeg];
  const failure = leg?.phase === 'error' ? leg.failure : undefined;
  const city = failure === 'timeout' ? wholeCityOf(state, places) : null;
  const failureText: FailureText = failure
    ? FAILURE_TEXT[failure](strings.page)
    : { title: strings.page.error, description: undefined };
  const copy =
    failure === 'search_expired'
      ? { ...emptyCopy, retryLabel: strings.page.searchExpiredAction }
      : emptyCopy;
  const retryable = !frozen && failure !== 'policy_evaluation';
  const text = city
    ? {
        title: strings.page.timeoutCity(places.localizedCity(city.metro)),
        description: strings.page.timeoutCityHint,
      }
    : failureText;
  if (!city || frozen) {
    return (
      <SearchEmptyState
        tone="error"
        title={text.title}
        description={text.description}
        copy={copy}
        {...(!frozen && onNewSearch ? { onNewSearch } : {})}
        {...(retryable ? { onRetry } : {})}
      />
    );
  }
  // Na cidade inteira os aeroportos são a saída principal: vêm logo abaixo do
  // texto, antes do "Nova busca" — no celular a página não cabe na dobra.
  return (
    <div className="flex flex-col items-center gap-4">
      <SearchEmptyState
        tone="error"
        title={text.title}
        description={text.description}
        copy={copy}
      />
      <div
        role="group"
        aria-label={strings.page.timeoutAirports}
        className="flex flex-wrap justify-center gap-2"
      >
        {city.airports.map((iata) => (
          <Button
            key={iata}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              onSearch({ ...city.params, [city.side]: iata });
            }}
          >
            {places.airportLabel(iata)}
          </Button>
        ))}
      </div>
      {onNewSearch ? (
        <Button type="button" variant="outline" size="lg" onClick={onNewSearch}>
          {copy.newSearchLabel}
        </Button>
      ) : null}
    </div>
  );
}
